import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";

import { AppLayout } from "@/layouts/app-layout";
import { LoginPage } from "@/features/auth/login-page";
import { CasesListPage } from "@/features/cases/cases-list-page";
import { CasePage } from "@/features/cases/case-page";
import { RouteFallback } from "@/components/route-fallback";
import { RequireRole, TEACHER_ROLES, ADMIN_ROLES } from "@/components/auth/require-role";
import { NotFoundPage } from "@/components/not-found-page";

// Lazy — rutas pesadas que no son la primera vista del usuario
const CaseSummaryPage = lazy(() =>
  import("@/features/cases/case-summary-page").then((m) => ({
    default: m.CaseSummaryPage,
  })),
);
const ActivePatientsPage = lazy(() =>
  import("@/features/cases/active-patients-page").then((m) => ({
    default: m.ActivePatientsPage,
  })),
);
const CognitivePage = lazy(() =>
  import("@/features/cognitive/cognitive-page").then((m) => ({
    default: m.CognitivePage,
  })),
);
const SrsPage = lazy(() =>
  import("@/features/learning/srs-page").then((m) => ({ default: m.SrsPage })),
);
const McqPage = lazy(() =>
  import("@/features/learning/mcq-page").then((m) => ({ default: m.McqPage })),
);
const EvidencePage = lazy(() =>
  import("@/features/learning/evidence-page").then((m) => ({
    default: m.EvidencePage,
  })),
);
const VivaPage = lazy(() =>
  import("@/features/game/viva-page").then((m) => ({ default: m.VivaPage })),
);
const AchievementsPage = lazy(() =>
  import("@/features/game/achievements-page").then((m) => ({
    default: m.AchievementsPage,
  })),
);
const TeacherDashboardPage = lazy(() =>
  import("@/features/institution/teacher-dashboard").then((m) => ({
    default: m.TeacherDashboardPage,
  })),
);
const CaseBuilderPage = lazy(() =>
  import("@/features/institution/case-builder-page").then((m) => ({
    default: m.CaseBuilderPage,
  })),
);
const AdminPage = lazy(() =>
  import("@/features/admin/admin-page").then((m) => ({
    default: m.AdminPage,
  })),
);
const AnatomyPage = lazy(() =>
  import("@/features/anatomy/anatomy-page").then((m) => ({
    default: m.AnatomyPage,
  })),
);
const SecurityPage = lazy(() =>
  import("@/features/account/security-page").then((m) => ({
    default: m.SecurityPage,
  })),
);

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  { path: "/", element: <Navigate to="/app" replace /> },
  { path: "/login", element: <LoginPage /> },
  {
    path: "/app",
    element: <AppLayout />,
    children: [
      { index: true, element: <Navigate to="/app/cases" replace /> },
      { path: "cases", element: <CasesListPage /> },
      { path: "cases/:encounterId", element: <CasePage /> },
      {
        path: "cases/:encounterId/summary",
        element: (
          <Lazy>
            <CaseSummaryPage />
          </Lazy>
        ),
      },
      {
        path: "active",
        element: (
          <Lazy>
            <ActivePatientsPage />
          </Lazy>
        ),
      },
      {
        path: "cognitive",
        element: (
          <Lazy>
            <CognitivePage />
          </Lazy>
        ),
      },
      {
        path: "srs",
        element: (
          <Lazy>
            <SrsPage />
          </Lazy>
        ),
      },
      {
        path: "mcq",
        element: (
          <Lazy>
            <McqPage />
          </Lazy>
        ),
      },
      {
        path: "evidence",
        element: (
          <Lazy>
            <EvidencePage />
          </Lazy>
        ),
      },
      {
        path: "anatomy",
        element: (
          <Lazy>
            <AnatomyPage />
          </Lazy>
        ),
      },
      {
        path: "viva",
        element: (
          <Lazy>
            <VivaPage />
          </Lazy>
        ),
      },
      {
        path: "achievements",
        element: (
          <Lazy>
            <AchievementsPage />
          </Lazy>
        ),
      },
      {
        path: "teacher",
        element: (
          <RequireRole allow={TEACHER_ROLES}>
            <Lazy>
              <TeacherDashboardPage />
            </Lazy>
          </RequireRole>
        ),
      },
      {
        path: "teacher/case-builder",
        element: (
          <RequireRole allow={TEACHER_ROLES}>
            <Lazy>
              <CaseBuilderPage />
            </Lazy>
          </RequireRole>
        ),
      },
      {
        path: "admin",
        element: (
          <RequireRole allow={ADMIN_ROLES}>
            <Lazy>
              <AdminPage />
            </Lazy>
          </RequireRole>
        ),
      },
      {
        path: "settings/security",
        element: (
          <Lazy>
            <SecurityPage />
          </Lazy>
        ),
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
  { path: "*", element: <NotFoundPage /> },
]);
